<script setup>
import { useLayout } from '@/layout/composables/layout';
import { computed, onBeforeUnmount, watch, ref } from 'vue';
import { useRoute } from 'vue-router';
import ModuleMaintenancePanel from '@/components/common/ModuleMaintenancePanel.vue';
import SupportWidget from '@/components/common/SupportWidget.vue';
import AppBreadcrumb from './AppBreadcrumb.vue';
import AppConfig from './AppConfig.vue';
import AppFooter from './AppFooter.vue';
import AppSidebar from './AppSidebar.vue';
import AppTopbar from './AppTopbar.vue';
import {
    getMaintenanceModule,
    getModuleLabel,
    isRouteUnderMaintenance,
} from '@/utils/maintenanceMode';
import { useUserSession } from '@/stores/userSession';

const { layoutConfig, layoutState, isSidebarActive } = useLayout();
const userSession = useUserSession();

const outsideClickListener = ref(null);
const route = useRoute();
function bindOutsideClickListener() {
    if (!outsideClickListener.value) {
        outsideClickListener.value = (event) => {
            if (isOutsideClicked(event)) {
                layoutState.overlayMenuActive = false;
                layoutState.overlaySubmenuActive = false;
                layoutState.staticMenuMobileActive = false;
                layoutState.menuHoverActive = false;
                layoutState.configSidebarVisible = false;
            }
        };
        document.addEventListener('click', outsideClickListener.value);
    }
}

function unbindOutsideClickListener() {
    if (outsideClickListener.value) {
        document.removeEventListener('click', outsideClickListener.value);
        outsideClickListener.value = null;
    }
}

function isOutsideClicked(event) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.topbar-menubutton');

    return !(sidebarEl.isSameNode(event.target) || sidebarEl.contains(event.target) || topbarEl.isSameNode(event.target) || topbarEl.contains(event.target));
}

watch(isSidebarActive, (newVal) => {
    if (newVal) {
        bindOutsideClickListener();
    } else {
        unbindOutsideClickListener();
    }
});

onBeforeUnmount(() => {
    unbindOutsideClickListener();
});

const containerClass = computed(() => {
    return [
        {
            'layout-light': layoutConfig.layoutTheme === 'colorScheme' && !layoutConfig.darkTheme,
            'layout-dark': layoutConfig.layoutTheme === 'colorScheme' && layoutConfig.darkTheme,
            'layout-primary': !layoutConfig.darkTheme && layoutConfig.layoutTheme === 'primaryColor',
            'layout-slim': layoutConfig.menuMode === 'slim',
            'layout-slim-plus': layoutConfig.menuMode === 'slim-plus',
            'layout-static': layoutConfig.menuMode === 'static',
            'layout-overlay': layoutConfig.menuMode === 'overlay',
            'layout-overlay-active': layoutState.overlayMenuActive,
            'layout-mobile-active': layoutState.staticMenuMobileActive,
            'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static'
        }
    ];
});
const contentClass = computed(() => {
    if (route.name === 'go-live-generator.index') {
        return '!layout-content !p-0'
    }
    if(route.name?.includes('patient-tile-composer.add') || route.name?.includes('patient-tile-composer.view')) {
        return 'p-2 lg:!p-0 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900'
    }
    // if(route.name?.includes('mp1-data-analyzer.analyzer')) {
    //     return 'p-2 lg:!p-0  dark:!bg-[#0a0a0b] !bg-gradient-subtle'
    // }
    return '!layout-content'
})

const showModuleMaintenance = computed(() =>
    isRouteUnderMaintenance(route, userSession.user.email)
);

const maintenanceModuleLabel = computed(() =>
    getModuleLabel(getMaintenanceModule(route))
);

const toastStyles = {
  messageIcon: {
    style: { 'margin-top': '3px' }
  }
}
</script>

<template>
    <div class="layout-container" :class="containerClass">
      <AppTopbar />
      <AppSidebar />

      <div class="layout-content-wrapper">
        <div class="layout-content" :class="contentClass">
          <div class="layout-content-inner">
            <!-- <AppBreadcrumb /> -->
            <ModuleMaintenancePanel
                v-if="showModuleMaintenance"
                :module-label="maintenanceModuleLabel"
            />
            <router-view v-else />
            <AppFooter />
          </div>
        </div>
      </div>

      <Toast :pt="toastStyles" />
      <AppConfig />
      <SupportWidget />
    </div>
  </template>

<style lang="scss"></style>
